import axios from 'axios';
import { Storage } from '@/Services/Storage';
import { fetchAdapterDebtors, fetchAdapterItems } from '@/Services/sapAdapterService';

/**
 * Vuex module: integrations/sap
 * - Retrieves and stores the SAP adapter API key for the current store.
 * - Fetches SAP adapter debtors list and exposes a quick lookup map.
 */
const state = () => ({
  sapApiKey: null,
  sapDebtors: [],
  sapItems: [],
  sapDebtorsFetchedAt: 0,
  sapItemsFetchedAt: 0,
});

const getters = {
  // Prefer camelCase as per adapter; fallback to legacy PascalCase for safety
  sapDebtorsMap: (s) => new Map((s.sapDebtors || []).map(d => [d.cardCode || d.CardCode, d])),
  sapItemsMap:   (s) => new Map((s.sapItems   || []).map(i => [i.itemCode || i.ItemCode, i])),
  isSapIntegrated: (s) => !!s.sapApiKey,
};

const mutations = {
  setSapApiKey(s, k) { s.sapApiKey = k || null; },
  setSapDebtors(s, items) { s.sapDebtors = Array.isArray(items) ? items : []; s.sapDebtorsFetchedAt = Date.now(); },
  setSapItems(s, items) { s.sapItems = Array.isArray(items) ? items : []; s.sapItemsFetchedAt = Date.now(); },
};

const SAP_TTL_MS = 5 * 60 * 1000; // 5 minutes

const actions = {
  /**
   * Fetch the SAP adapter API key for the current store DB from SubscriptionManager.
   * Uses global window.subscriptionTomcatApi.
   *
   * @param {Object} context - Vuex context
   * @param {string} [db] - Optional store database name. If omitted, derived from Storage.getCurrentStoreDB()
   * @returns {Promise<string|null>} The apiKey or null when not integrated
   */
  async fetchSapApiKey({ commit }, db) {
    try {
      let storeDB = db;
      if (!storeDB) storeDB = await Storage.getCurrentStoreDB();
      if (!storeDB && window.storeDB) storeDB = window.storeDB;
      if (!storeDB) {
        commit('setSapApiKey', null);
        return null;
      }
      const base = window.subscriptionTomcatApi || window.subscriptionApi;
      const url = `${base}integrations/checkSapRegistration/${encodeURIComponent(storeDB)}`;
      const { data } = await axios.get(url);
      const apiKey = data && data.apikey ? data.apikey : null;
      commit('setSapApiKey', apiKey);
      return apiKey;
    } catch (e) {
      commit('setSapApiKey', null);
      // surface minimal error while staying junior-friendly
      console.warn('Failed to fetch SAP API key:', e && e.message ? e.message : e);
      return null;
    }
  },

  /**
   * Fetch a page of SAP adapter debtors. Only runs if apiKey is present.
   * @param {Object} context
   * @param {Object} [params]
   * @returns {Promise<Array>} items
   */
  async fetchAdapterDebtors({ commit, state }, params = { page: 1, pageSize: 100000 }) {
    const apiKey = state.sapApiKey;
    if (!apiKey) { commit('setSapDebtors', []); return []; }
    const { items = [] } = await fetchAdapterDebtors(apiKey, params).catch(e => {
      console.warn('SAP adapter /debtors error:', e && e.message ? e.message : e);
      return { items: [] };
    });
    commit('setSapDebtors', items);
    return items;
  },

  /**
   * Fetch a page of SAP adapter items. Only runs if apiKey is present.
   * @param {Object} context
   * @param {Object} [params]
   * @returns {Promise<Array>} items
   */
  async fetchAdapterItems({ commit, state }, params = { page: 1, pageSize: 100000 }) {
    const apiKey = state.sapApiKey;
    if (!apiKey) { commit('setSapItems', []); return []; }
    const { items = [] } = await fetchAdapterItems(apiKey, params).catch(e => {
      console.warn('SAP adapter /items error:', e && e.message ? e.message : e);
      return { items: [] };
    });
    commit('setSapItems', items);
    return items;
  },

  /**
   * Fetch SAP debtors only if needed (not fetched recently and cache present)
   */
  async fetchAdapterDebtorsIfNeeded({ dispatch, state }, { page = 1, pageSize = 100000, force = false } = {}) {
    const apiKey = state.sapApiKey;
    if (!apiKey) return [];
    const fresh = Date.now() - (state.sapDebtorsFetchedAt || 0) < SAP_TTL_MS;
    if (!force && fresh && (state.sapDebtors?.length || 0) > 0) return state.sapDebtors;
    return await dispatch('fetchAdapterDebtors', { page, pageSize });
  },

  /**
   * Fetch SAP items only if needed (not fetched recently and cache present)
   */
  async fetchAdapterItemsIfNeeded({ dispatch, state }, { page = 1, pageSize = 100000, force = false } = {}) {
    const apiKey = state.sapApiKey;
    if (!apiKey) return [];
    const fresh = Date.now() - (state.sapItemsFetchedAt || 0) < SAP_TTL_MS;
    if (!force && fresh && (state.sapItems?.length || 0) > 0) return state.sapItems;
    return await dispatch('fetchAdapterItems', { page, pageSize });
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};